import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TourOptionImageManager from "./TourOptionImageManager";
import { IDMCTourOption } from "commonFnBcode/types/dmc.types";
import { activitySearchRtk } from "activityModule/apis/activitySearchRtk";

const SortableItem = ({ item }: { item: IDMCTourOption }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || item.sort_no });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: "5px",
    backgroundColor: "#fff",
    borderRadius: "5px",
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    gap: "4px",
    position: "relative" as const,
    zIndex: isDragging ? 999 : 1,
    width: "100%",
    boxShadow: isDragging
      ? "0 4px 8px rgba(0,0,0,0.15)"
      : "0 1px 3px rgba(0,0,0,0.1)",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        {...listeners}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          padding: "8px 4px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          display: "flex",
          alignItems: "center",
          fontSize: "12px",
          color: "#666",
          flexShrink: 0,
          touchAction: "none",
        }}
      >
        <i className="fas fa-grip-vertical" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <TourOptionImageManager tourOption={item} />
      </div>
    </div>
  );
};

function DragActivityItem({ tourOptions }: { tourOptions: IDMCTourOption[] }) {
  const [items, setItems] = React.useState<IDMCTourOption[]>([]);
  const [activeItem, setActiveItem] = React.useState<IDMCTourOption | null>(
    null
  );

  React.useEffect(() => {
    const sortedTourOptions = tourOptions
      .slice()
      .sort((a, b) => a.sort_no - b.sort_no);
    setItems(sortedTourOptions);
  }, [tourOptions]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const [changeOrder] =
    activitySearchRtk.useUpdateTourOptionAndImageOrderMutation();

  const handleDragStart = (event: DragStartEvent) => {
    const activeItemData = items.find((item) => item.id === event.active.id);
    setActiveItem(activeItemData || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return setActiveItem(null);

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return setActiveItem(null);

    const newItems = arrayMove(items, oldIndex, newIndex);
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sort_no: index + 1,
    }));

    setItems(updatedItems);
    setActiveItem(null);

    const sortedIds = updatedItems.map((item, index) => ({
      id: item.id!,
      sort_no: index + 1,
    }));

    changeOrder({
      order: sortedIds,
      order_for: "tourOptions",
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id || item.sort_no)}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item) => (
          <SortableItem key={item.id || `temp-${item.sort_no}`} item={item} />
        ))}
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <div
            style={{
              opacity: 0.95,
              display: "flex",
              gap: "4px",
              backgroundColor: "#fff",
              borderRadius: "5px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              transform: "rotate(2deg)",
            }}
          >
            {/* Drag handle in overlay */}
            <div
              style={{
                cursor: "grabbing",
                padding: "8px 4px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                fontSize: "12px",
                color: "#666",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <i className="fas fa-grip-vertical" />
            </div>
            {/* Content in overlay */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <TourOptionImageManager tourOption={activeItem} />
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default DragActivityItem;
